import { apiRequest, toQueryString } from '../apiRequest';
import type { Project } from '../../../types/api/projects';
import type { Repository } from '../../../types/api/repositories';

type ReposListResponse = { repositories?: Repository[]; total?: number };
type DiscoveredReposResponse = {
  discoveredRepositories?: Repository[];
  message?: string;
  meta?: Record<string, unknown>;
};
type BranchesResponse = { branches?: string[]; defaultBranch?: string };
type ConnectRepoResponse = { repository?: Repository };
type ProjectsListResponse = { projects?: Project[] };

export const organisationsRepositoriesAPI = {
  getRepositories: async (orgId: string, params: Record<string, unknown> = {}) => {
    const qs = toQueryString(params);
    const data = await apiRequest<ReposListResponse>(
      `/api/organisations/${orgId}/repositories${qs}`,
      { method: 'GET' },
    );
    return { repositories: data.repositories ?? [], total: data.total ?? 0 };
  },
  getDiscoveredRepositories: async (orgId: string, params: Record<string, unknown> = {}) => {
    const qs = toQueryString(params);
    const data = await apiRequest<DiscoveredReposResponse>(
      `/api/organisations/${orgId}/discovered-repositories${qs}`,
      { method: 'GET' },
    );
    return { discoveredRepositories: data.discoveredRepositories ?? [], message: data.message };
  },
  refreshDiscoveredRepositories: async (orgId: string) => {
    const data = await apiRequest<DiscoveredReposResponse>(
      `/api/organisations/${orgId}/discovered-repositories/refresh`,
      { method: 'POST' },
    );
    return {
      discoveredRepositories: data.discoveredRepositories ?? [],
      meta: data.meta,
    };
  },
  getDiscoveredRepositoriesDropdown: async (orgId: string) => {
    const data = await apiRequest<{ repositories?: Repository[] }>(
      `/api/organisations/${orgId}/discovered-repositories/dropdown`,
      { method: 'GET' },
    );
    return data.repositories ?? [];
  },
  getRepositoryBranches: async (orgId: string, repository?: string) => {
    const qs = repository ? `?repository=${encodeURIComponent(repository)}` : '';
    const data = await apiRequest<BranchesResponse>(
      `/api/organisations/${orgId}/repositories/branches${qs}`,
      { method: 'GET' },
    );
    return { branches: data.branches ?? [], defaultBranch: data.defaultBranch ?? 'main' };
  },
  connectRepositoryToProject: async (
    orgId: string,
    {
      projectId,
      repository,
      deploymentBranches,
      qaBranch,
      uatBranch,
    }: {
      projectId: string;
      repository: string;
      deploymentBranches: string[];
      qaBranch?: string;
      uatBranch?: string;
    },
  ) => {
    const data = await apiRequest<ConnectRepoResponse | Repository>(
      `/api/organisations/${orgId}/repositories/connect-to-project`,
      {
        method: 'POST',
        body: JSON.stringify({ projectId, repository, deploymentBranches, qaBranch, uatBranch }),
      },
    );
    return ('repository' in data && data.repository ? data.repository : data) as Repository;
  },
  getProjects: async (orgId: string, params: Record<string, unknown> = {}) => {
    const qs = toQueryString(params);
    const data = await apiRequest<ProjectsListResponse>(
      `/api/organisations/${orgId}/projects${qs}`,
      { method: 'GET' },
    );
    return data.projects ?? [];
  },
};
